import React, { useState, useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { Swiper } from 'swiper/types';
import { useMomentsDataFetching } from './useMomentsDataFetching';
import { IMomentsContext, MomentApplication } from './moments.types';
import {
  momentCanBeClaimed,
  useBannerAnimation,
  getMomentStorageKey,
  momentIsDismissed,
  productListProductToGlobalProduct,
} from './momentsUtils';
import { setStorageValue } from '../../../utils/storage';

export const MomentsContext = React.createContext<IMomentsContext>({
  moments: [],
  className: '',
  momentsForClaim: [],
  momentsForDismiss: [],
  isLoading: false,
  isClaiming: false,
  handleClaimReward: (_moment: MomentApplication) => void 0,
  dismissReward: () => void 0,
  setSwiperRef: () => void 0,
  onAnimationEnd: () => void 0,
  productList: null,
  isProductListModalOpen: false,
  handleOpenProductListModal: () => {},
  handleCloseProductListModal: () => {},
  handleClaimProductListReward: () => {},
});

MomentsContext.displayName = 'MomentsContext';

export const useMomentsContext = (): IMomentsContext =>
  React.useContext<IMomentsContext>(MomentsContext);

type MomentsProviderProps = {
  subscriptionId: string;
  refetchSubscription: ({
    compareOnlyLines,
  }: {
    compareOnlyLines?: boolean;
  }) => void;
  showErrorToast: (message: string) => void;
};

export const MomentsContextProvider = ({
  value,
  children,
}: {
  value: MomentsProviderProps;
  children: React.ReactElement | React.ReactElement[];
}): React.ReactElement => {
  const { animate, onAnimationEnd, className } = useBannerAnimation();
  const [swiperRef, setSwiperRef] = useState<null | Swiper>(null);
  const [isProductListModalOpen, setIsProductListModalOpen] = useState(false);
  const [productList, setProductList] = useState<GlobalProduct[] | null>(null);
  const [selectedMoment, setSelectedMoment] =
    useState<MomentApplication | null>(null);
  const { subscriptionId, showErrorToast, refetchSubscription } = value;
  const { mutate, moments, isLoading, isClaiming } = useMomentsDataFetching(
    subscriptionId,
    showErrorToast,
    animate,
  );
  const handleOpenProductListModal = useCallback(() => {
    setIsProductListModalOpen(true);
  }, []);

  const handleCloseProductListModal = useCallback(() => {
    setIsProductListModalOpen(false);
    setProductList(null);
  }, []);

  const handleClaimProductListReward = useCallback(
    (productVariantGId: string) => {
      setIsProductListModalOpen(false);
      setProductList(null);
      const momentId = selectedMoment?.id;
      mutate({ momentId, productVariantGId });
      setSelectedMoment(null);
      refetchSubscription({});
    },
    [mutate, refetchSubscription, selectedMoment?.id],
  );

  const handleClaimReward = useCallback(
    (moment: MomentApplication) => {
      const productList = moment.moment.actions?.find(
        ({ type, metadata }) =>
          type === 'PRODUCT' && metadata?.productVariantList?.length > 0,
      );
      if (swiperRef?.slideTo && !productList) {
        const index = moments.findIndex((m) => m.id === moment.id);
        swiperRef.slideTo(index);
      }

      if (productList) {
        setSelectedMoment(moment);
        const productsToShow = moment.moment.availableProductList.map(
          (product) => {
            const globalProduct = productListProductToGlobalProduct(product);
            const imgs = product?.images?.map(({ url }) => url);

            return {
              ...globalProduct,
              imgs,
            };
          },
        );

        setProductList(productsToShow);
        setIsProductListModalOpen(true);
      } else {
        mutate({ momentId: moment.id });
        refetchSubscription({});
      }
    },
    [moments, mutate, swiperRef, refetchSubscription],
  );

  const dismissReward = useCallback((moment: MomentApplication['moment']) => {
    const storageKey = getMomentStorageKey(subscriptionId, moment.id);

    const isReearnable = !!moment?.isReearnable;
    const isClaimable = !!moment?.canBeClaimed;

    // If a moment is claimable, don't dismiss permanently
    if (!isClaimable) {
      const value = isReearnable ? DateTime.utc().toISO() : true;
      setStorageValue(storageKey, value);
    }
  }, []);

  const momentsForClaim = useMemo(() => {
    return moments.filter((moment) => {
      const isClaimable = momentCanBeClaimed(moment);
      const isDismissed = momentIsDismissed(subscriptionId, moment);

      return isClaimable && !isDismissed;
    });
  }, [moments, subscriptionId]);

  const momentsForDismiss = useMemo(() => {
    return moments.filter((moment) => {
      const isAutoClaimable = (moment?.moment?.actions?.[0] as any)
        ?.isAutomatic;
      // If moment is claimable or already claimed, filter out
      if (
        !moment?.isEarned ||
        moment?.canBeClaimed ||
        (!moment?.canBeClaimed && !isAutoClaimable) ||
        !moment?.moment?.claimBannerMetadata
      ) {
        return false;
      }

      const isDismissed = momentIsDismissed(subscriptionId, moment);

      // Only leave moments that are not dismissed
      return !isDismissed;
    });
  }, [moments, subscriptionId]);

  return (
    <MomentsContext.Provider
      value={{
        moments,
        momentsForClaim,
        momentsForDismiss,
        handleClaimReward,
        dismissReward,
        setSwiperRef,
        onAnimationEnd,
        className,
        isLoading,
        isClaiming,
        productList,
        isProductListModalOpen,
        handleOpenProductListModal,
        handleCloseProductListModal,
        handleClaimProductListReward,
      }}
    >
      {children}
    </MomentsContext.Provider>
  );
};
